import React, { useState, useEffect } from 'react';
import { auditApi } from '../../lib/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { formatDate } from '../../lib/utils';
import { 
  FileText, Loader2, User, Building2, Sparkles, LogIn, UserPlus,
  Shield, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const actionIcons = {
  LOGIN: LogIn,
  REGISTER: UserPlus,
  CREATE_ENTITY: Building2,
  UPDATE_ENTITY: Building2,
  DELETE_ENTITY: Building2,
  AI_ANALYSIS: Sparkles,
  COMPLETE_ONBOARDING: Shield
};

const actionColors = {
  LOGIN: 'text-blue-400 bg-blue-400/20',
  REGISTER: 'text-emerald-400 bg-emerald-400/20',
  CREATE_ENTITY: 'text-purple-400 bg-purple-400/20',
  UPDATE_ENTITY: 'text-amber-400 bg-amber-400/20',
  DELETE_ENTITY: 'text-red-400 bg-red-400/20',
  AI_ANALYSIS: 'text-amber-400 bg-amber-400/20',
  COMPLETE_ONBOARDING: 'text-emerald-400 bg-emerald-400/20'
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await auditApi.getLogs(100);
      setLogs(res.data);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-log-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">Audit Log</h1>
            <p className="text-slate-400 mt-1">Track all actions and changes in your account</p>
          </div>
          <Button
            onClick={loadLogs}
            variant="outline"
            className="border-slate-700 text-slate-300"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Logs List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="stat-card text-center py-12" data-testid="no-logs">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No audit logs yet</h3>
            <p className="text-slate-400">Actions will be recorded here as you use CorpCraft</p>
          </div>
        ) : (
          <div className="stat-card p-0 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800 text-left">
                  <th className="px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Entity Type</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Details</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Hash</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {logs.map((log) => {
                  const Icon = actionIcons[log.action] || FileText;
                  const colorClass = actionColors[log.action] || 'text-slate-400 bg-slate-400/20';
                  return (
                    <tr key={log.id} className="hover:bg-slate-800/30" data-testid={`log-${log.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass.split(' ')[1]}`}>
                            <Icon className={`w-4 h-4 ${colorClass.split(' ')[0]}`} />
                          </div>
                          <span className="font-medium text-sm">{log.action.replace(/_/g, ' ')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-400">{log.entity_type}</td>
                      <td className="px-6 py-4 text-sm text-slate-400 max-w-xs truncate">
                        {log.details?.name || log.details?.industry || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-xs text-slate-500 font-mono bg-slate-800 px-2 py-1 rounded">
                          {log.hash?.substring(0, 12)}...
                        </code>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-400">
                        {formatDate(log.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
